export function getScreenScopes(roles = []) {
  const scopes = {
    all: false,
    siteIds: new Set(),
    locationIds: new Set(),
    sublocationIds: new Set(),
  };

  for (const raw of roles) {
    const role = String(raw || "").trim();

    if (role === "superadmin" || role === "screens:all") scopes.all = true;

    const siteMatch = role.match(/^screens:site:(\d+)$/);
    if (siteMatch) scopes.siteIds.add(Number(siteMatch[1]));

    const locMatch = role.match(/^screens:location:(\d+)$/);
    if (locMatch) scopes.locationIds.add(Number(locMatch[1]));

    const subMatch = role.match(/^screens:sublocation:(\d+)$/);
    if (subMatch) scopes.sublocationIds.add(Number(subMatch[1]));
  }

  return scopes;
}

export function getAllowedScreenData({ roles = [], sites = [], locations = [], sublocations = [], units = [] }) {
  const scopes = getScreenScopes(roles);

  const isAllowedLocation = (locationId) => {
    if (scopes.all) return true;
    if (locationId == null) return false;
    return scopes.locationIds.has(Number(locationId));
  };

  const isAllowedSublocation = (sublocationId) => {
    if (scopes.all) return true;
    if (sublocationId == null) return false;
    return scopes.sublocationIds.has(Number(sublocationId));
  };

  const allowedSites = scopes.all
    ? sites
    : sites.filter((site) => {
        const siteId = Number(site.id);

        if (scopes.siteIds.has(siteId)) return true;

        const hasAllowedLocation = locations.some(
          (location) => Number(location.site_id) === siteId && isAllowedLocation(location.id)
        );
        if (hasAllowedLocation) return true;

        return sublocations.some((sublocation) => {
          if (!isAllowedSublocation(sublocation.id)) return false;
          const parentLocation = locations.find((location) => location.id === sublocation.location_id);
          return parentLocation ? Number(parentLocation.site_id) === siteId : false;
        });
      });

  const allowedLocations = scopes.all
    ? locations
    : locations.filter((location) => {
        const locationId = Number(location.id);
        const siteId = Number(location.site_id);

        if (scopes.locationIds.has(locationId) || scopes.siteIds.has(siteId)) return true;

        return sublocations.some(
          (sublocation) =>
            Number(sublocation.location_id) === locationId && isAllowedSublocation(sublocation.id)
        );
      });

  const allowedSublocations = scopes.all
    ? sublocations
    : sublocations.filter((sublocation) => {
        const sublocationId = Number(sublocation.id);
        const locationId = Number(sublocation.location_id);
        const parentLocation = locations.find((location) => location.id === sublocation.location_id);
        const siteId = parentLocation ? Number(parentLocation.site_id) : null;

        return (
          scopes.sublocationIds.has(sublocationId) ||
          scopes.locationIds.has(locationId) ||
          (siteId != null && scopes.siteIds.has(siteId))
        );
      });

  const allowedUnits = scopes.all
    ? units
    : units.filter((unit) => {
        const siteId = unit.site_id ?? unit.siteId;
        const locationId = unit.location_id ?? unit.locationId;
        const sublocationId = unit.sublocation_id ?? unit.sublocationId;

        return (
          (siteId != null && scopes.siteIds.has(Number(siteId))) ||
          (locationId != null && scopes.locationIds.has(Number(locationId))) ||
          (sublocationId != null && scopes.sublocationIds.has(Number(sublocationId)))
        );
      });

  return {
    scopes,
    allowedSites,
    allowedLocations,
    allowedSublocations,
    allowedUnits,
  };
}
